import { Grid } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import { Controller, useFieldArray, useFormContext } from 'react-hook-form';

import { getDefaultSkillSet } from '@AMIEWEB/Order/OrderCreationForm/Components/UnitDetail/UnitDetailDefaultValues';
import { AndOrSwitchButton } from '@AMIEWEB/Order/OrderCreationForm/Components/UnitDetail/UnitRequirements/Experience/AndOrButtonComponents';
import { AndOrOperator, CollectionType } from 'app/enums/Common';
import { AndorButton } from './Experience/AndorButton';
import { TravelExperienceTile } from './Experience/TravelExperienceTile';
import { OrderLicenseGridWrapper } from './OrderLicenseGridWrapper';
import { OrderSkillSetSection } from './OrderSkillSetSection';
import { CertificationsTile } from './Experience/CertificationsTile';
import { orderDataActions } from '@AMIEWEB/Order/Store/Order.redux';
import { useDispatch, useSelector } from 'react-redux';
import { useDecision } from '@optimizely/react-sdk';
import { selectFacility } from '@AMIEWEB/Facility/FacilityStore/Facility.selector';
import { AMNDivisionType } from 'app/models/enums/AMNDivisionType';
import { ff_order_ui_allow_duplicate_skillsets } from 'app/constants/FeatureFlags/Order/Keys';

const useStyles = makeStyles()(theme => ({
  container: {
    border: `1px solid ${theme.palette.system.lightGrey}`,
    borderRadius: '4px',
    backgroundColor: theme.palette.system.white,
    '& .MuiFilledInput-root:hover.Mui-disabled:before': {
      borderBottom: '1px solid rgba(0, 0, 0, 0.42)!important',
    },
    '& .MuiFilledInput-root.Mui-disabled': {
      backgroundColor: theme.palette.system.grayChip,
      opacity: '0.8',
    },
    '& .MuiFilledInput-root.Mui-disabled:before': {
      borderBottomStyle: 'solid',
    },
  },
  buttonContainer: {
    display: 'grid',
    justifyItems: 'center',
  },
}));

export const OrderExperienceSet = ({ groupIndex, groupField, setExperienceCount }) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const { control, watch, setValue } = useFormContext();
  const operationValue = watch(`skillSetGroup.groups[${groupIndex}].operation`);
  const [duplicateCetificateData, setDuplicateCetificateData] = useState(null);
  const [canAddCertificate, setCanAddCertificate] = useState(false);
  const [enableDuplicateSkillSet] = useDecision(ff_order_ui_allow_duplicate_skillsets);
  const facilityDetails = useSelector(selectFacility);
  const facilityDivision =
    facilityDetails?.facilityResponse?.amnDivisionTypeId || facilityDetails?.response?.amnDivisionTypeId;

  const {
    fields: skillSetFields,
    append: appendSkillSet,
    remove: removeSkillSet,
  } = useFieldArray({
    control,
    name: `skillSetGroup.groups[${groupIndex}].skillsets`,
  });

  const handleAdd = () => {
    appendSkillSet(getDefaultSkillSet(skillSetFields.length));
    setCanAddCertificate(true);
  };

  const handleDuplicate = ({ skillSetId }) => {
    if (enableDuplicateSkillSet.enabled && skillSetId > -1) {
      const selectedSkillSet = watch(`skillSetGroup.groups[${groupIndex}].skillsets`)[skillSetId];
      const updatedValue = {
        ...selectedSkillSet,
        id: skillSetFields.length,
        isDuplicate: true,
        isPrimary: false,
        skillSet: { ...selectedSkillSet.skillSet },
      };

      appendSkillSet(updatedValue);
    } else {
      const lastSkillSetValue = watch(`skillSetGroup.groups[${groupIndex}].skillsets`)[skillSetFields.length - 1];
      const updatedValue = {
        ...lastSkillSetValue,
        id: skillSetFields.length,
        isDuplicate: true,
        isPrimary: false,
        skillSet: { ...lastSkillSetValue?.skillSet, specialty: null, subSpecialty: null },
      };
      appendSkillSet(updatedValue);
    }

  };


  const updatePrimarySkillset = () => {
    setTimeout(() => {
      setValue(`skillSetGroup.groups[${groupIndex}].skillsets[${0}].isPrimary`, true);
    }, 100);
  };

  const handleRemove = skillSetIndex => {
    const isDeletedSkillsetPrimary = watch(`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].isPrimary`);
    removeSkillSet(skillSetIndex);
    if (isDeletedSkillsetPrimary) updatePrimarySkillset();
  };

  useEffect(() => {
    setExperienceCount(skillSetFields.length);
  }, [skillSetFields.length]);

  useEffect(() => {
    setValue(`skillSetGroup.groups[${groupIndex}].operation`, operationValue ?? AndOrOperator.Or);
  }, []);

  const updateSelectedSubCerts = useCallback(() => {
    const selectedSubCerts = [];
    skillSetFields?.forEach((x, idx) => {
      const subCerts = x?.collections[1]?.requirements
        ?.map(each => each?.subCertificate?.[0]?.subCertificate?.id)
        ?.filter(Boolean);
      selectedSubCerts.push({ skillSetIndex: idx, subCerts: subCerts });
    });

    dispatch(orderDataActions.setSelectedSubCert(selectedSubCerts));
  }, [dispatch, skillSetFields]);

  useEffect(() => {
    updateSelectedSubCerts();
  }, [skillSetFields, updateSelectedSubCerts]);

  return (
    <>
      {skillSetFields?.map((skillSetField, skillSetIndex) => {
        return (
          <Grid key={skillSetField.id}>
            <Grid container className={classes.container}>
              <OrderSkillSetSection
                skillSetIndex={skillSetIndex}
                groupIndex={groupIndex}
                skillSetField={skillSetField}
                onRemove={handleRemove}
              />
              <TravelExperienceTile skillSetIndex={skillSetIndex} groupIndex={groupIndex} value={skillSetField} />
              <OrderLicenseGridWrapper skillSetIndex={skillSetIndex} groupIndex={groupIndex} value={skillSetField} />
              <CertificationsTile
                skillSetIndex={skillSetIndex}
                groupIndex={groupIndex}
                skillSetField={skillSetField}
                duplicateCetificateData={duplicateCetificateData}
                canAddCertificate={canAddCertificate}
                setCanAddCertificate={setCanAddCertificate}
                numberOfSkillSets={skillSetFields.length}
                setDuplicateCetificateData={setDuplicateCetificateData}
              />
            </Grid>
            <Grid className={classes.buttonContainer}>
              {skillSetFields.length - 1 === skillSetIndex ? (
                <AndorButton
                  handleAdd={handleAdd}
                  handleDuplicate={handleDuplicate}
                  value={skillSetFields.length > 1 && operationValue}
                  groupIndex={groupIndex}
                  skillSetIndex={skillSetIndex}
                  facilityDivision={facilityDivision}
                />
              ) : (
                <Controller
                  name={`skillSetGroup.groups[${groupIndex}].operation`}
                  control={control}
                  defaultValue={operationValue}
                  render={({ field }) => (
                    <AndOrSwitchButton
                      value={operationValue}
                      onChange={field.onChange}
                      isDisabled={facilityDivision === AMNDivisionType.amnNurse}
                    />
                  )}
                />
              )}
            </Grid>
          </Grid>
        );
      })}
    </>
  );
};
